import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DEFAULT_SHELL_ID,
  SHELL_REGISTRY,
  registerBuiltInShells,
} from '../../shells/registry';
import type { Shell, ShellId } from '../../shells/types';

const STORAGE_KEY = 'alds-shell';
const BODY_ATTR = 'data-shell';

interface ShellContextValue {
  shell: Shell;
  allShells: Shell[];
  /** Replace the active shell. Resolves after the lazy theme import. */
  setShell: (id: ShellId) => Promise<void>;
}

const ShellContext = createContext<ShellContextValue | null>(null);

function readStoredId(): ShellId {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SHELL_REGISTRY.has(v)) return v;
  } catch { /* noop */ }
  return DEFAULT_SHELL_ID;
}

function writeStoredId(id: ShellId): void {
  try { localStorage.setItem(STORAGE_KEY, id); } catch { /* noop */ }
}

async function applyShell(shell: Shell): Promise<void> {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute(BODY_ATTR, shell.id);
  }
  if (shell.themeImporter) {
    try { await shell.themeImporter(); } catch { /* noop — base tokens still apply */ }
  }
}

export function ShellProvider({ children }: { children: React.ReactNode }): JSX.Element {
  // Ensure builtins are registered before we read the initial id.
  registerBuiltInShells();

  const [shellId, setShellId] = useState<ShellId>(() => readStoredId());

  // Apply the shell (body attr + lazy CSS) whenever id changes.
  useEffect(() => {
    const shell = SHELL_REGISTRY.get(shellId) ?? SHELL_REGISTRY.get(DEFAULT_SHELL_ID)!;
    void applyShell(shell);
  }, [shellId]);

  const setShell = useCallback(async (id: ShellId) => {
    if (!SHELL_REGISTRY.has(id)) {
      throw new Error(`unknown shell: ${id}`);
    }
    writeStoredId(id);
    setShellId(id);
    const shell = SHELL_REGISTRY.get(id)!;
    await applyShell(shell);
  }, []);

  const value = useMemo<ShellContextValue>(() => {
    const shell = SHELL_REGISTRY.get(shellId) ?? SHELL_REGISTRY.get(DEFAULT_SHELL_ID)!;
    return { shell, allShells: SHELL_REGISTRY.all(), setShell };
  }, [shellId, setShell]);

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellContextValue {
  const v = useContext(ShellContext);
  if (!v) throw new Error('useShell must be called inside a <ShellProvider>');
  return v;
}

/**
 * Test-only helper that resets persisted shell selection. The provider
 * itself never clears localStorage — switching to the default shell is
 * an explicit user action.
 */
export function __resetShellForTests(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  if (typeof document !== 'undefined') {
    document.documentElement.removeAttribute(BODY_ATTR);
  }
}
